import React, {useCallback, useEffect, useMemo, useState} from 'react';
import _ from 'lodash';
import VStack from '../../../../../common/Layout/VStack';
import {fetchApi} from '../../../../../../services/HTTP';
import {sortString} from '../../../../../../services/Helpers';
import usePermissions from '../../../../../../services/Permissions';
import CourseLimitationsSkeleton from './CourseLimitationsSkeleton';
import FilterTags from '../../../../../common/FilterTags';
import GroupedCheckboxTable from '../../../../../common/GroupedCheckboxTable';
import {SESSION_PER_WEEK_COURSE_OPTIONS} from '../../../../../../../web/src/components/Track/TrackConsts';
import {FREQUENCY_COLUMN_LABEL_KEYS} from '../../../../../../../web/src/components/Track/TrackConsts';
import {
    getCategoryGroup,
    getServiceType,
    getSeriesEventsCount,
    groupCoursesForTable,
} from './CourseLimitationsHelpers';

const CourseLimitations = ({values, setFieldValue}) => {
    const {has} = usePermissions();
    const showCategoryFilter = useMemo(() => has('boxCategoriesGroups') && !has('boxCategoriesGroupsLock'), [has]);
    const [hugimList, setHugimList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [selectedServiceTypeId, setSelectedServiceTypeId] = useState(null);

    const selectedCoursesById = useMemo(() => (values.courses ?? []).reduce((acc, course) => {
        if (course?.series_id) {
            acc[course.series_id] = course.session_per_week ?? null;
        }
        return acc;
    }, {}), [values.courses]);

    const fetchHugimForTrack = useCallback(async () => {
        if (!values.locations_box_fk) {
            setHugimList([]);
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            const res = await fetchApi('seriesTrack/getHugimForTracks', 'POST', {
                locations_box_id: values.locations_box_fk,
                start_date: values.start_date,
                end_date: values.end_date,
            }, false, true);
            setHugimList(res ?? []);
            setIsLoading(false);
        } catch (error) {
            console.error(error);
            setHugimList([]);
            setIsLoading(false);
        }
    }, [values.locations_box_fk, values.start_date, values.end_date]);

    useEffect(() => {
        fetchHugimForTrack();
    }, [fetchHugimForTrack]);

    useEffect(() => {
        setSelectedCategoryId(null);
        setSelectedServiceTypeId(null);
    }, [values.locations_box_fk]);

    const categoryOptions = useMemo(() => {
        if (!showCategoryFilter) return [];
        const groups = hugimList.map(getCategoryGroup).filter(category => !!category);
        return _.sortBy(_.uniqBy(groups, 'id'), group => sortString(group.name));
    }, [hugimList, showCategoryFilter]);

    const serviceTypeOptions = useMemo(() => {
        const baseList = selectedCategoryId
            ? hugimList.filter(course => getCategoryGroup(course)?.id === selectedCategoryId)
            : hugimList;
        const serviceTypes = baseList.map(getServiceType).filter(serviceType => !!serviceType);
        return _.sortBy(_.uniqBy(serviceTypes, 'id'), serviceType => sortString(serviceType.name));
    }, [hugimList, selectedCategoryId]);

    const filteredCourses = useMemo(() => hugimList.filter(course => {
        const matchesCategory = !selectedCategoryId || getCategoryGroup(course)?.id === selectedCategoryId;
        const matchesServiceType = !selectedServiceTypeId || getServiceType(course)?.id === selectedServiceTypeId;
        return matchesCategory && matchesServiceType;
    }), [hugimList, selectedCategoryId, selectedServiceTypeId]);

    const tableGroups = useMemo(
        () => groupCoursesForTable(filteredCourses),
        [filteredCourses, showCategoryFilter, categoryOptions.length],
    );

    const tableColumns = useMemo(() => SESSION_PER_WEEK_COURSE_OPTIONS.map(frequency => ({
        key: frequency,
        labelKey: FREQUENCY_COLUMN_LABEL_KEYS[frequency],
    })), []);

    const checkboxTableGroups = useMemo(() => tableGroups.map(group => ({
        key: group.key,
        title: group.serviceTypeName,
        rows: group.courses.map(course => ({
            key: course.id,
            label: course.series_name,
            selectedColumnKey: selectedCoursesById[course.id] ?? null,
            isColumnDisabled: (columnKey) => columnKey > getSeriesEventsCount(course) || columnKey > values.session_per_week,
        })),
    })), [tableGroups, selectedCoursesById]);

    const onFrequencySelect = (seriesId, frequency) => {
        const currentCourses = values.courses ?? [];
        const existingCourse = currentCourses.find(course => course.series_id === seriesId);
        if (existingCourse?.session_per_week === frequency) {
            setFieldValue('courses', currentCourses.filter(course => course.series_id !== seriesId));
            return;
        }
        const nextCourses = existingCourse
            ? currentCourses.map(course => course.series_id === seriesId
                ? {...course, session_per_week: frequency}
                : course)
            : [...currentCourses, {series_id: seriesId, session_per_week: frequency}];
        setFieldValue('courses', nextCourses);
    };

    const onCategoryTagClick = (categoryId) => {
        setSelectedCategoryId(prev => prev === categoryId ? null : categoryId);
        setSelectedServiceTypeId(null);
    };

    const onServiceTypeTagClick = (serviceTypeId) => {
        setSelectedServiceTypeId(prev => prev === serviceTypeId ? null : serviceTypeId);
    };

    if (isLoading) {
        return <CourseLimitationsSkeleton showCategoryFilter={showCategoryFilter && categoryOptions.length > 0} />;
    }

    return (
        <VStack space={'0.75rem'} flex={1} width={'100%'}>
            {showCategoryFilter && categoryOptions.length > 0 &&
                <FilterTags
                    labelKey={'courseLimitationsFilterByCategory'}
                    options={categoryOptions}
                    selectedId={selectedCategoryId}
                    onTagClick={onCategoryTagClick}
                />
            }
            <VStack space={'1.5rem'}>
                <FilterTags
                    labelKey={'courseLimitationsFilterByServiceType'}
                    options={serviceTypeOptions}
                    selectedId={selectedServiceTypeId}
                    onTagClick={onServiceTypeTagClick}
                />
                <GroupedCheckboxTable
                    nameColumnLabelKey={'courseLimitationsCourseName'}
                    columns={tableColumns}
                    groups={checkboxTableGroups}
                    emptyMessageKey={'no-hugim-found'}
                    disabledTooltipKey={'courseLimitationsDisabledFrequencyTooltip'}
                    onSelect={onFrequencySelect}
                />
            </VStack>

        </VStack>
    );
};

export default CourseLimitations;
